import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { MuiButtonAppearanceEnum, MuiModalService } from '@move/ui';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HasUnsavedChanges } from '../../data-loss-protection/has-unsaved-changes';


/**
 * Functional Guard for checking unsaved changes in a component. The component class must implement the HasUnsavedChanges interface.
 * If the component has unsaved changes, a confirmation modal will be shown to the user.
 *
 * @returns CanDeactivateFn<HasUnsavedChanges>
 */
export const hasUnsavedChanges: () => CanDeactivateFn<HasUnsavedChanges> = () => {
  return (component: HasUnsavedChanges): Promise<boolean> | boolean => {
    const modalService = inject(MuiModalService);
    const translateService = inject(TranslateService);

    const openConfirmationModal = (): NgbModalRef => {
      const titleKey = 'global.error.unsavedChanges.title';
      const msgKey = 'global.error.unsavedChanges.message';
      const confirmKey = 'global.error.unsavedChanges.confirmBtn';
      const abortKey = 'global.error.unsavedChanges.abortBtn';
      const tl = translateService.instant([titleKey, msgKey, confirmKey, abortKey]);

      return modalService.openBasicModal(tl[msgKey], tl[titleKey], {
        backdrop: 'static',
        confirmButton: {
          label: tl[confirmKey],
          appearance: MuiButtonAppearanceEnum.DANGER,
          returnValue: true
        },
        dismissButton: {
          label: tl[abortKey],
          appearance: MuiButtonAppearanceEnum.SECONDARY,
          returnValue: false
        },
        keyboard: false
      });
    };

    // Check if Component has implemented the interface
    if (component.hasUnsavedChanges !== undefined) {
      if (component.hasUnsavedChanges()) {
        const ref = openConfirmationModal();
        return ref.result.then(
          () => true,
          () => false
        );
      } else {
        return true;
      }
    } else {
      const name = component.constructor && component.constructor.name ? component.constructor.name + ' ' : '';
      console.warn(`UnsavedChangesGuard: Provided Component ${name}used by this guard does not correctly implement
      the interface HasUnsavedChanges. Navigation will be permitted regardless of any unsaved changes.`);
      return true;
    }
  };
};
